import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { catalogosService } from '../../services/catalogosService';
import { Linea, Ramo } from '../../types';
import { SelectChangeEvent } from '@mui/material/Select';

const LineaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoLinea } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [formData, setFormData] = useState<Linea>({
    codigoLinea: '',
    nombreLinea: '',
    codigoRamo: '',
    estadoLinea: 'ACTIVO',
    articuloXlistaPrecios: [],
    clases: [],
    descuentos: [],
    stocks: []
  });

  const loadRamos = useCallback(async () => {
    try {
      const response = await catalogosService.getRamos(1, 1000);
      if (response && response.items) {
        setRamos(response.items);
      } else {
        setRamos([]);
        enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error al cargar ramos:', error);
      setRamos([]);
      enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadLinea = useCallback(async () => {
    try {
      setLoading(true);
      if (!codigoLinea) return;
      const linea = await catalogosService.getLinea(codigoLinea);
      setFormData(linea);
    } catch (error) {
      console.error('Error al cargar la línea:', error);
      enqueueSnackbar('Error al cargar la línea', { variant: 'error' });
      navigate('/inventario/lineas');
    } finally {
      setLoading(false);
    }
  }, [codigoLinea, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadRamos();
    if (codigoLinea) {
      loadLinea();
    }
  }, [codigoLinea, loadRamos, loadLinea]);

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked ? 'ACTIVO' : 'INACTIVO'
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      // Crear el objeto completo de tipo Linea
      const lineaData: Linea = {
        codigoLinea: formData.codigoLinea.trim(),
        nombreLinea: formData.nombreLinea.trim(),
        codigoRamo: formData.codigoRamo.trim(),
        estadoLinea: formData.estadoLinea,
        // Incluir las propiedades requeridas con arrays vacíos
        articuloXlistaPrecios: [],
        clases: [],
        descuentos: [],
        stocks: []
      };

      if (codigoLinea) {
        await catalogosService.updateLinea(codigoLinea, lineaData);
      } else {
        await catalogosService.createLinea(lineaData);
      }
      
      enqueueSnackbar('Línea guardada exitosamente', { variant: 'success' });
      navigate('/inventario/lineas');
    } catch (error) {
      console.error('Error al guardar la línea:', error);
      enqueueSnackbar('Error al guardar la línea', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            {codigoLinea ? 'Editar Línea' : 'Nueva Línea'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="codigoLinea"
                  label="Código"
                  value={formData.codigoLinea}
                  onChange={handleTextFieldChange}
                  fullWidth
                  required
                  disabled={!!codigoLinea}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="nombreLinea"
                  label="Nombre"
                  value={formData.nombreLinea}
                  onChange={handleTextFieldChange}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="ramo-select-label">Ramo</InputLabel>
                  <Select
                    labelId="ramo-select-label"
                    name="codigoRamo"
                    value={formData.codigoRamo}
                    onChange={handleSelectChange}
                    label="Ramo"
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {ramos.map((ramo) => (
                      <MenuItem key={ramo.codigoRamo} value={ramo.codigoRamo}>
                        {ramo.nombreRamo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estadoLinea === 'ACTIVO'}
                      onChange={handleSwitchChange}
                      name="estadoLinea"
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/lineas')}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LineaForm; 